import React from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider } from "@clerk/react";
import { passkeys } from "@clerk/electron/passkeys";
import { ClerkProvider as ElectronClerkProvider } from "@clerk/electron/react";
import { createHashHistory, createBrowserHistory } from "@tanstack/react-router";

import "@fontsource-variable/dm-sans/index.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@xterm/xterm/css/xterm.css";
import "./index.css";

import { isElectron } from "./env";
import { ManagedRelayAuthProvider } from "./cloud/managedAuth";
import { resolveClerkProviderConfig } from "./cloud/clerkRuntime";
import { getRouter } from "./router";
import {
  syncDocumentElectronPlatformClasses,
  syncDocumentWindowControlsOverlayClass,
} from "./lib/windowControlsOverlay";
import { AppRoot } from "./AppRoot";

// Electron loads the app from a file-backed shell, so hash history avoids path resolution issues.
const history = isElectron ? createHashHistory() : createBrowserHistory();

const router = getRouter(history);

if (isElectron) {
  syncDocumentElectronPlatformClasses(navigator.platform);
  syncDocumentWindowControlsOverlayClass();
}

const app = <AppRoot router={router} />;

// One resolved decision rather than three predicates read in sequence: whether a
// provider mounts at all, whether Clerk is reached through the hosted Worker's
// same-origin proxy, and whether the T3 Connect relay session belongs above the
// app. The relay session needs a relay URL and JWT template on top of the key,
// which the hosted deployment does not have, so those are separate questions.
const clerkConfig = resolveClerkProviderConfig();

const authenticatedApp = (() => {
  if (!clerkConfig) {
    return app;
  }
  const authenticated = clerkConfig.relayAuth ? (
    <ManagedRelayAuthProvider>{app}</ManagedRelayAuthProvider>
  ) : (
    app
  );
  // Electron reaches Clerk directly from its own shell, so it never carries a
  // proxy URL; `resolveClerkProviderConfig` already guarantees that.
  return isElectron ? (
    <ElectronClerkProvider publishableKey={clerkConfig.publishableKey} passkeys={passkeys}>
      {authenticated}
    </ElectronClerkProvider>
  ) : (
    <ClerkProvider
      publishableKey={clerkConfig.publishableKey}
      {...(clerkConfig.proxyUrl ? { proxyUrl: clerkConfig.proxyUrl } : {})}
    >
      {authenticated}
    </ClerkProvider>
  );
})();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>{authenticatedApp}</React.StrictMode>,
);
